import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import {
  countBulkBannerCompleted,
  expandProcessingIdsForBulkTargets,
  reconcileQueueIds,
} from '@/lib/bulkProcessQueueSync';
import { useAdaptiveBulkQueuePoll } from '@/lib/bulkQueuePollScheduler';
import { syncBulkQueueProgress } from '@/lib/syncBulkQueueProgress';
import { queryKeys } from '@/lib/queryKeys';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import { useApplicationsQuery } from '@/hooks/useRecruiterListQueries';
import type { Application } from '@/types';

/**
 * Keeps PostUpgradeBanner "processed N of K" in sync while bulk screening runs,
 * even when the user leaves the Applications page (queue persisted in sessionStorage).
 */
export function useBulkProcessBannerSync(companyId: string | undefined): void {
  const queryClient = useQueryClient();
  const applicationsQuery = useApplicationsQuery(companyId);
  const applications = applicationsQuery.data ?? [];
  const applicationsReady = !applicationsQuery.isFetching;
  const bulkPhase = useBulkProcessBannerStore((s) => s.phase);
  const startBulkRun = useBulkProcessBannerStore((s) => s.startBulkRun);
  const syncProgress = useBulkProcessBannerStore((s) => s.syncProgress);
  const sessionBatch = useMemo(
    () => (companyId ? loadBulkProcessQueue(companyId).targetIds.length > 0 : false),
    [companyId, bulkPhase],
  );
  const active = !!companyId && (bulkPhase === 'active' || sessionBatch);

  // Resume banner after navigation while session still has an in-flight bulk batch.
  useEffect(() => {
    if (!companyId || bulkPhase === 'active') return;
    const loaded = loadBulkProcessQueue(companyId);
    if (loaded.targetIds.length === 0) return;

    const apps =
      queryClient.getQueryData<Application[]>(queryKeys.applications(companyId, false))
      ?? [];
    const expandedProcessing = expandProcessingIdsForBulkTargets(
      loaded.targetIds,
      loaded.processingIds,
      apps,
      { includeUnknownTargets: apps.length > 0 },
    );
    const bulkScope = new Set(loaded.targetIds);
    const settled = countBulkBannerCompleted(
      loaded.targetIds,
      apps,
      expandedProcessing,
      loaded.stoppingIds,
      bulkScope,
    );
    const initialCompleted = Math.max(loaded.completed, settled);

    if (initialCompleted < loaded.targetIds.length) {
      startBulkRun(companyId, loaded.targetIds, {
        resume: true,
        initialCompleted,
      });
    }

    const cachedApps =
      queryClient.getQueryData<Application[]>(queryKeys.applications(companyId, false))
      ?? [];
    void syncBulkQueueProgress(queryClient, companyId, syncProgress, {
      targetIds: loaded.targetIds,
      forceListRefetch: cachedApps.length === 0,
      resumeHydrate: true,
    });
  }, [companyId, bulkPhase, queryClient, startBulkRun, syncProgress]);

  // Reconcile banner when applications list data updates (fetch, hydrate, patch).
  useEffect(() => {
    if (!companyId || bulkPhase !== 'active' || !applicationsReady || applications.length === 0) return;

    const banner = useBulkProcessBannerStore.getState();
    if (banner.companyId !== companyId || banner.targetIds.length === 0) return;

    const loaded = loadBulkProcessQueue(companyId);
    const batchScope = new Set(banner.targetIds);
    const reconciled = reconcileQueueIds(
      applications,
      loaded.stoppingIds,
      loaded.processingIds,
      { bulkTargetIds: batchScope },
    );
    const expandedProcessing = expandProcessingIdsForBulkTargets(
      banner.targetIds,
      reconciled.processingIds,
      applications,
      { includeUnknownTargets: true },
    );
    syncProgress(companyId, expandedProcessing, reconciled.stoppingIds, applications);
  }, [companyId, bulkPhase, applicationsReady, applications, syncProgress]);

  useAdaptiveBulkQueuePoll(active, companyId, queryClient, syncProgress);
}
